import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { ROLE_HOME } from '../utils/roleHome';
import { toast } from 'sonner';
import axios from 'axios';
import {
  GraduationCap,
  School,
  Target,
  Building2,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  ChevronDown,
  Check,
  Search,
  ShieldCheck,
  CheckCircle,
  KeyRound,
  Layers,
  BookOpen,
  Users,
  CheckCircle2,
  UserCheck,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { DEFAULT_INSTITUTE_CATEGORIES, buildCategoryTree, matchCategoryRoot } from '../utils/instituteCategories';
import ThemeToggle from '../components/ThemeToggle';
import { captureAttribution, clearAttribution } from '../utils/signupAttribution';
import Turnstile, { useTurnstileSiteKey } from '../components/security/Turnstile';
import AuthShowcase from '../components/auth/AuthShowcase';

const LOGIN_HOME = {
  super_admin: '/admin',
  institute_admin: '/institute-panel',
  teacher: '/teacher',
  staff: '/institute-panel/crm',
  student: '/student',
  parent: '/parent',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Archetype Data (School, College, Coaching) ──
const ARCHETYPES = [
  {
    id: 'school',
    name: 'School',
    categoryName: 'School',
    label: 'School',
    icon: School,
    badge: 'K-12, CBSE, ICSE & State Boards',
    headline: 'Complete School ERP & Academic Management',
    tagline: 'Smart Attendance, CBSE/ICSE Report Cards & Parent Communication',
    description: 'Empower principals, teachers, parents, and students with automated fee tracking, timetable scheduling, biometric attendance, and instant report card generation.',
    placeholderName: 'School Name (e.g., Delhi Public School)',
    placeholderEmail: 'School Email (e.g., principal@school.edu)',
    stats: [
      { val: 'CBSE & ICSE', label: 'Board Compliant' },
      { val: 'Smart App', label: 'Parent & Homework' },
      { val: 'Report Card', label: '1-Click Marks Entry' },
      { val: 'Fee Alerts', label: 'Zero-Dues Reminders' },
    ],
    features: [
      'Automated CBSE/ICSE Term Report Cards & Marks Entry',
      'Daily Parent Homework, Circulars & Attendance Alerts',
      'Class & Section Scheduling with Subject Teachers',
      'Online Fee Collection with Instant WhatsApp Invoices',
    ],
    accentBg: 'bg-indigo-500/15 border-indigo-400/30 text-brand-gold',
    pillActive: 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30',
  },
  {
    id: 'college',
    name: 'College',
    categoryName: 'College',
    label: 'College',
    icon: GraduationCap,
    badge: 'Colleges, Universities & Campuses',
    headline: 'Enterprise Campus ERP for Colleges & Universities',
    tagline: 'Multi-Department Faculty, Semester Credits & Student Portals',
    description: 'Streamline university admissions, semester credits, departmental faculty allocation, student digital IDs, and transparent installment fee structures.',
    placeholderName: 'College or University Name (e.g., Apex Institute of Technology)',
    placeholderEmail: 'Campus Email (e.g., dean@apexcollege.edu)',
    stats: [
      { val: 'CBCS & CGPA', label: 'Semester Grading' },
      { val: 'Departments', label: 'Faculty Matrix' },
      { val: 'Student ID', label: 'Digital Gate Pass' },
      { val: 'Multi-Campus', label: 'Central Head-Office' },
    ],
    features: [
      'Semester Credits, Course Electives & CGPA Calculators',
      'Departmental Faculty Load, HRMS & Biometric Logs',
      'Digital Student Gate Passes & ID Card Generation',
      'Multi-Installment Semester Fee & Scholarship Management',
    ],
    accentBg: 'bg-sky-500/15 border-sky-400/30 text-sky-300',
    pillActive: 'bg-sky-600 text-white shadow-md shadow-sky-600/30',
  },
  {
    id: 'coaching_institute',
    name: 'Coaching Institute',
    categoryName: 'Coaching Institute',
    label: 'Coaching',
    icon: Target,
    badge: 'Test Prep, IIT-JEE, NEET & Tuitions',
    headline: 'Complete Coaching & Test Prep Management',
    tagline: 'Batch Scheduling, OMR Analytics, Lead CRM & Instant WhatsApp Fees',
    description: 'Run high-performance batches, mock test series, OMR answer sheet scanners, automated WhatsApp fee receipts, and lead inquiry funnels all in one place.',
    placeholderName: 'Coaching or Academy Name (e.g., Apex Career Classes)',
    placeholderEmail: 'Coaching Email (e.g., admin@apexclasses.com)',
    stats: [
      { val: 'OMR & Tests', label: 'Rank Analysis' },
      { val: 'Batch Engine', label: 'Flexible Shifts' },
      { val: 'WhatsApp', label: 'Instant Receipts' },
      { val: 'Lead CRM', label: 'Inquiry Funnel' },
    ],
    features: [
      'Batch-wise Attendance & Flexible Time Shift Allocations',
      'AI & OMR Test Evaluation with All-India Rank Analysis',
      'Instant WhatsApp Fee Invoices, Dues & Payment Links',
      'Walk-in Inquiries & Demo Admission CRM Pipeline',
    ],
    accentBg: 'bg-indigo-500/15 border-indigo-400/30 text-brand-gold',
    pillActive: 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30',
  },
];

// ── Standard Text Input Box (Label-free, icon-equipped, unified h-12) ──
function StandardInput({
  icon: Icon,
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  error = null,
  autoComplete,
  dataTestId,
  rightElement = null,
  ariaLabel,
}) {
  return (
    <div className="w-full">
      <div
        className={`group relative flex items-center w-full h-12 rounded-xl border bg-slate-50/70 hover:bg-slate-50 dark:bg-slate-800/80 dark:hover:bg-slate-800 transition-all duration-200 ${
          error
            ? 'border-rose-400 bg-rose-50/25 dark:bg-rose-950/20 ring-2 ring-rose-500/10 focus-within:border-rose-500 focus-within:ring-rose-500/20'
            : 'border-slate-200/90 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:border-indigo-600 dark:focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-500/10'
        } ${disabled ? 'opacity-60 cursor-not-allowed bg-slate-100/80 dark:bg-slate-800/50' : ''}`}
      >
        {Icon && (
          <div className="pl-3.5 pr-2.5 flex items-center pointer-events-none text-slate-400 dark:text-slate-400 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors">
            <Icon className="w-4 h-4 shrink-0" />
          </div>
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          data-testid={dataTestId}
          aria-label={ariaLabel || placeholder}
          className="w-full h-full bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 font-normal outline-none pr-3.5"
        />
        {rightElement && <div className="pr-3 flex items-center">{rightElement}</div>}
      </div>
      {error && (
        <p className="text-xs text-rose-500 dark:text-rose-400 mt-1 font-medium flex items-center gap-1.5 animate-fadeIn">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 inline-block" />
          {error}
        </p>
      )}
    </div>
  );
}

// ── Standard Custom Dropdown (Unified h-12, searchable, premium popover) ──
function StandardDropdown({
  value,
  onChange,
  options = [],
  placeholder = 'Select option...',
  icon: DefaultIcon = Layers,
  error = null,
  disabled = false,
  dataTestId,
  searchable = true,
  ariaLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find(
      (opt) =>
        opt.value === value ||
        opt.name === value ||
        opt.id === value ||
        String(opt.slug) === String(value)
    );
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((opt) => {
      const label = opt.label || opt.name || opt.value || '';
      const desc = opt.description || opt.desc || '';
      return label.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
    });
  }, [options, search]);

  const SelectedIcon = selectedOption?.iconComponent || DefaultIcon;

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        data-testid={dataTestId}
        aria-label={ariaLabel || placeholder}
        aria-expanded={isOpen}
        className={`group relative flex items-center justify-between w-full h-12 px-3.5 rounded-xl border bg-slate-50/70 hover:bg-slate-50 dark:bg-slate-800/80 dark:hover:bg-slate-800 transition-all duration-200 text-left cursor-pointer ${
          error
            ? 'border-rose-400 bg-rose-50/25 dark:bg-rose-950/20 ring-2 ring-rose-500/10'
            : isOpen
            ? 'bg-white dark:bg-slate-800 border-indigo-600 dark:border-indigo-400 ring-4 ring-indigo-500/10 shadow-sm'
            : 'border-slate-200/90 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800/50' : ''}`}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <SelectedIcon
            className={`w-4 h-4 shrink-0 transition-colors ${
              selectedOption ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-400 group-hover:text-slate-500'
            }`}
          />
          <span
            className={`text-sm truncate ${
              selectedOption ? 'text-slate-900 dark:text-slate-100 font-medium' : 'text-slate-400 dark:text-slate-500 font-normal'
            }`}
          >
            {selectedOption?.label || selectedOption?.name || placeholder}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180 text-indigo-600 dark:text-indigo-400' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/60 dark:shadow-black/60 p-1.5 max-h-72 flex flex-col animate-fadeIn">
          {searchable && options.length > 5 && (
            <div className="p-1 border-b border-slate-100 dark:border-slate-800 mb-1">
              <div className="relative flex items-center">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search options..."
                  className="w-full h-8 pl-8 pr-2 text-xs bg-slate-50 dark:bg-slate-800 rounded-lg border-0 outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:ring-1 focus:ring-indigo-400"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto max-h-60 scrollbar-thin divide-y divide-slate-50 dark:divide-slate-800/60">
            {filteredOptions.length === 0 ? (
              <div className="py-4 text-center text-xs text-slate-400 font-medium">No options found</div>
            ) : (
              filteredOptions.map((opt) => {
                const optVal = opt.value ?? opt.name ?? opt.id;
                const optLabel = opt.label ?? opt.name ?? String(optVal);
                const isSelected = value === optVal;
                const ItemIcon = opt.iconComponent || DefaultIcon;
                return (
                  <button
                    key={optVal}
                    type="button"
                    onClick={() => {
                      onChange(optVal);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left text-xs sm:text-sm transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-950/70 text-indigo-900 dark:text-indigo-300 font-semibold'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}
                      >
                        <ItemIcon className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{optLabel}</div>
                        {opt.description && (
                          <div className="text-[11px] text-slate-400 dark:text-slate-500 truncate font-normal mt-0.5">
                            {opt.description}
                          </div>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0 ml-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-rose-500 dark:text-rose-400 mt-1 font-medium flex items-center gap-1.5 animate-fadeIn">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 inline-block" />
          {error}
        </p>
      )}
    </div>
  );
}

// ── Forgot Password Modal ──
function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  // The security check appears only after the server asks for it (captcha_required)
  const captchaSiteKey = useTurnstileSiteKey();
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaRound, setCaptchaRound] = useState(0);
  const [captchaNeeded, setCaptchaNeeded] = useState(false);
  const captchaVisible = Boolean(captchaSiteKey) && captchaNeeded;

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email, captcha_token: captchaToken || undefined });
      if (res.data.token) {
        setToken(res.data.token);
        setStep('reset');
        toast.info('Demo mode: Token auto-filled. Enter your new password.');
      } else {
        setStep('done');
        toast.success('Reset link sent to your email!');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reset email');
      if (e.response?.data?.captcha_required) setCaptchaNeeded(true);
      if (captchaSiteKey) {
        setCaptchaToken('');
        setCaptchaRound((n) => n + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword) || !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/.test(newPassword)) {
      toast.error('Password must contain uppercase, lowercase, a number, and a special character');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid or expired token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-6 border border-slate-100 dark:border-slate-800 transition-colors">
        {step === 'email' && (
          <>
            <div className="text-center mb-5">
              <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/60 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Mail className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="font-cabinet font-bold text-xl text-slate-900 dark:text-white">Forgot Password?</h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">Enter your registered email address</p>
            </div>
            <form onSubmit={handleRequestReset} className="space-y-3.5">
              <StandardInput
                icon={Mail}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Registered email address"
                dataTestId="forgot-email-input"
              />
              <Turnstile siteKey={captchaVisible ? captchaSiteKey : ''} onToken={setCaptchaToken} resetKey={captchaRound} />
              <button
                type="submit"
                disabled={loading || (captchaVisible && !captchaToken)}
                data-testid="forgot-submit-btn"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition-all shadow-sm disabled:opacity-60 cursor-pointer"
              >
                {loading ? 'Sending link...' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full text-slate-500 dark:text-slate-400 text-xs hover:text-slate-700 dark:hover:text-slate-200 font-medium cursor-pointer py-1"
              >
                Cancel & Return
              </button>
            </form>
          </>
        )}

        {step === 'reset' && (
          <>
            <div className="text-center mb-5">
              <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/60 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <KeyRound className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="font-cabinet font-bold text-xl text-slate-900 dark:text-white">Set New Password</h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">Paste reset token & choose new password</p>
            </div>
            <form onSubmit={handleReset} className="space-y-3">
              <StandardInput
                icon={KeyRound}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste token from email"
                dataTestId="reset-token-input"
              />
              <StandardInput
                icon={Lock}
                type={showPwd ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password (min 8 chars, A-Z, 0-9 & symbol)"
                dataTestId="new-password-input"
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-btn"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition-all shadow-sm disabled:opacity-60 cursor-pointer mt-2"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        {step === 'done' && (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-100 dark:border-emerald-900/60 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-cabinet font-bold text-xl text-slate-900 dark:text-white mb-2">Password Reset Done!</h3>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-6">Your password has been updated. You can now login.</p>
            <button
              onClick={onClose}
              data-testid="reset-done-btn"
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm cursor-pointer shadow-sm"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main AuthPage ──
export default function AuthPage({ mode: defaultMode }) {
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');
  const resolvedMode = defaultMode || (modeParam === 'register' ? 'register' : 'login');
  const [authMode, setAuthMode] = useState(resolvedMode);
  const [portalRole] = useState(() => {
    const p = searchParams.get('portal') || searchParams.get('role');
    if (p === 'parent') return 'parent';
    if (p === 'student') return 'student';
    return 'all';
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // Archetype selection (School, College, Coaching)
  const [activeArchetypeId, setActiveArchetypeId] = useState('school');

  // Categories list & selection
  const [categoriesList, setCategoriesList] = useState(DEFAULT_INSTITUTE_CATEGORIES);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'institute_admin',
    phone: '',
    category: 'School',
    subcategory: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});
  // Referral code / "Powered by" link / directory listing claim this visitor arrived with.
  const [attribution] = useState(() => captureAttribution(searchParams));
  const [claimListing, setClaimListing] = useState(null);

  useEffect(() => {
    if (!attribution.claim_listing_id) return;
    axios.get(`${API}/marketplace/institutes/${attribution.claim_listing_id}`)
      .then((res) => {
        if (!res.data?.is_claimable) return;
        setClaimListing(res.data);
        setForm((prev) => ({ ...prev, name: prev.name || res.data.name || '' }));
      })
      .catch(() => {});
  }, [attribution.claim_listing_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { login, completeTwoFactor, register, user: sessionUser, loading: sessionLoading } = useAuth();
  // Two-step sign-in: the password was right, the authenticator code is next
  const [twoFactor, setTwoFactor] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  // Cloudflare Turnstile, when the backend has its keys: a fresh token for every attempt.
  // Register always shows it; sign-in only after the server asks (captcha_required).
  const captchaSiteKey = useTurnstileSiteKey();
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaRound, setCaptchaRound] = useState(0);
  const [loginCaptchaNeeded, setLoginCaptchaNeeded] = useState(false);
  const captchaVisible = Boolean(captchaSiteKey) && (authMode !== 'login' || loginCaptchaNeeded);
  const navigate = useNavigate();
  const submittingRef = useRef(false);

  // Already signed in (e.g. the installed app opened on /login): go straight to the dashboard.
  useEffect(() => {
    if (resolvedMode === 'login' && !sessionLoading && ROLE_HOME[sessionUser?.role] && !submittingRef.current) {
      navigate('/dashboard', { replace: true });
    }
  }, [resolvedMode, sessionLoading, sessionUser, navigate]);

  // Load Categories from Backend
  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        const res = await axios.get(`${API}/auth/categories`);
        if (isMounted && Array.isArray(res.data) && res.data.length > 0) {
          const tree = buildCategoryTree(res.data);
          if (tree && tree.length > 0) {
            setCategoriesList(tree);
          }
        }
      } catch (err) {
        // Fallback gracefully to default institute categories
      } finally {
        if (isMounted) setCategoriesLoading(false);
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  // Sync mode from prop/URL
  useEffect(() => {
    setAuthMode(resolvedMode);
  }, [resolvedMode]);

  // Load Saved Remember Me
  useEffect(() => {
    if (authMode !== 'login') return;
    const saved = localStorage.getItem('coachhub_remember_me');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.email) {
        setForm((prev) => ({
          ...prev,
          email: parsed.email,
        }));
        setRememberMe(true);
        // Purge plaintext password if saved by earlier versions
        if (parsed.password) {
          localStorage.setItem('coachhub_remember_me', JSON.stringify({ email: parsed.email }));
        }
      }
    } catch {
      localStorage.removeItem('coachhub_remember_me');
    }
  }, [authMode]);

  const [cachedBranding] = useState(() => {
    try {
      const b = localStorage.getItem('institute_branding');
      return b ? JSON.parse(b) : null;
    } catch {
      return null;
    }
  });

  // Current active archetype object
  const currentArchetype = useMemo(() => {
    return ARCHETYPES.find((a) => a.id === activeArchetypeId) || ARCHETYPES[0];
  }, [activeArchetypeId]);

  // Match root category for subcategories
  const selectedCategoryObj = useMemo(() => {
    if (!form.category) return null;
    return matchCategoryRoot(categoriesList, form.category);
  }, [categoriesList, form.category]);

  const availableSubcategories = selectedCategoryObj?.subcategories || [];

  // Dropdown options prepared for StandardDropdown
  const categoryDropdownOptions = useMemo(() => {
    return categoriesList.map((cat) => {
      const iconComp =
        cat.name.toLowerCase().includes('school')
          ? School
          : cat.name.toLowerCase().includes('college') || cat.name.toLowerCase().includes('university')
          ? GraduationCap
          : Target;
      return {
        value: cat.name,
        name: cat.name,
        label: cat.name,
        description: cat.description || '',
        iconComponent: iconComp,
      };
    });
  }, [categoriesList]);

  const subcategoryDropdownOptions = useMemo(() => {
    return availableSubcategories.map((sub) => ({
      value: sub.name,
      name: sub.name,
      label: sub.name,
      description: '',
      iconComponent: BookOpen,
    }));
  }, [availableSubcategories]);

  // Archetype Switch Handler
  const handleSelectArchetype = (archId) => {
    const arch = ARCHETYPES.find((a) => a.id === archId);
    if (!arch) return;
    setActiveArchetypeId(arch.id);
    setForm((prev) => ({
      ...prev,
      category: arch.categoryName,
      subcategory: '',
    }));
    if (fieldErrors.category) {
      setFieldErrors((prev) => ({ ...prev, category: null }));
    }
  };

  // Category Dropdown Change Handler
  const handleCategoryDropdownChange = (newCatName) => {
    setForm((prev) => ({
      ...prev,
      category: newCatName,
      subcategory: '',
    }));
    // Sync archetype pill
    const lower = (newCatName || '').toLowerCase();
    if (lower.includes('school')) {
      setActiveArchetypeId('school');
    } else if (lower.includes('college') || lower.includes('university')) {
      setActiveArchetypeId('college');
    } else {
      setActiveArchetypeId('coaching_institute');
    }
    if (fieldErrors.category) {
      setFieldErrors((prev) => ({ ...prev, category: null }));
    }
  };

  const handleSubcategoryDropdownChange = (newSubName) => {
    setForm((prev) => ({
      ...prev,
      subcategory: newSubName,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // After a sign-in (password, or password and code): remember the email, send a pending claim
  const afterLogin = async (user) => {
    if (rememberMe) {
      localStorage.setItem('coachhub_remember_me', JSON.stringify({ email: form.email }));
    } else {
      localStorage.removeItem('coachhub_remember_me');
    }
    if (claimListing && user?.role === 'institute_admin') {
      // An existing owner who logged in from a listing's "Claim" button.
      try {
        await axios.post(`${API}/growth/claims`, { listing_id: claimListing.slug });
        toast.info(`${claimListing.name} ka claim review ke liye bhej diya gaya.`);
      } catch (claimErr) {
        toast.error(claimErr.response?.data?.detail || 'Listing claim nahi ho paaya');
      }
      clearAttribution();
    }
  };

  const handleTwoFactor = async (e) => {
    e.preventDefault();
    if (!twoFactorCode.trim()) return;
    setTwoFactorBusy(true);
    submittingRef.current = true;
    try {
      const user = await completeTwoFactor(twoFactor.challenge, twoFactorCode.trim());
      await afterLogin(user);
      setTwoFactor(null);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(LOGIN_HOME[user.role] || '/');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not verify the code';
      toast.error(detail);
      // The 5-minute sign-in ran out: back to the password
      if (/expired/i.test(detail)) setTwoFactor(null);
      setTwoFactorCode('');
    } finally {
      submittingRef.current = false;
      setTwoFactorBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    submittingRef.current = true;

    try {
      let user;
      if (authMode === 'login') {
        const result = await login(form.email, form.password, { captcha_token: captchaToken || undefined });
        if (result?.twoFactorRequired) {
          setTwoFactorCode('');
          setTwoFactor({ challenge: result.challenge });
          return;
        }
        user = result;
        await afterLogin(user);
      } else {
        if (!form.name || !form.email || !form.password) {
          toast.error('Please fill all required fields');
          setLoading(false);
          return;
        }
        if (form.password.length < 8) {
          toast.error('Password must be at least 8 characters long');
          setLoading(false);
          return;
        }
        if (!/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/\d/.test(form.password) || !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/.test(form.password)) {
          toast.error('Password must include uppercase, lowercase, number, and special character');
          setLoading(false);
          return;
        }
        if (!form.category) {
          toast.error('Please select an institute category');
          setLoading(false);
          return;
        }
        user = await register(
          form.name,
          form.email,
          form.password,
          form.role,
          form.phone,
          form.category,
          form.subcategory || '',
          { ...attribution, captcha_token: captchaToken || undefined }
        );
        if (claimListing) {
          // Signup never fails because of a claim, so check that the claim really went in.
          try {
            const mine = await axios.get(`${API}/growth/claims/mine`);
            const sent = (mine.data?.claims || []).some((c) => (c.listing_slug === claimListing.slug || c.listing_id === claimListing.id) && c.status === 'pending');
            if (sent) toast.info(`${claimListing.name} ka claim review ke liye bhej diya gaya. Status "Refer & Earn" page par dikhega.`);
            else toast.error(`${claimListing.name} ka claim nahi ban paaya. "Refer & Earn" page se dobara try karein.`);
          } catch { /* the account is ready; the claim status is on the Refer & Earn page */ }
        }
        clearAttribution();
      }

      toast.success(`Welcome${authMode === 'login' ? ' back' : ''}, ${user.name}!`);
      let home = LOGIN_HOME[user.role] || '/';
      if (user.role === 'super_admin') {
        // Super admins turn on two-step sign-in before the panel opens
        try {
          const twoStep = await axios.get(`${API}/auth/2fa/status`);
          if (twoStep.data?.required && !twoStep.data?.enabled) home = '/setup-two-step';
        } catch { /* the panel redirects there anyway when needed */ }
      }
      navigate(home);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.message ||
        (authMode === 'login' ? 'Login failed' : 'Registration failed');
      toast.error(detail);
      if (authMode === 'login' && err.response?.data?.captcha_required) {
        setLoginCaptchaNeeded(true);
      }
      if (authMode === 'register') {
        const newErrs = {};
        if (/name/i.test(detail)) newErrs.name = detail;
        if (/email/i.test(detail)) newErrs.email = detail;
        if (/phone/i.test(detail)) newErrs.phone = detail;
        if (Object.keys(newErrs).length > 0) {
          setFieldErrors(newErrs);
        }
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
      // A Turnstile token works once
      if (captchaSiteKey) {
        setCaptchaToken('');
        setCaptchaRound((n) => n + 1);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/70 dark:bg-slate-950 flex flex-col lg:flex-row overflow-x-hidden selection:bg-indigo-500 selection:text-white">
      <Helmet>
        <title>{authMode === 'register' ? 'Register your institute free | CoachHub' : 'Log in | CoachHub'}</title>
        <meta
          name="description"
          content={authMode === 'register'
            ? 'Create your CoachHub account: ERP for schools, colleges and coaching institutes. 14-day free trial, no card required.'
            : 'Log in to CoachHub as an institute owner, teacher, student or parent.'}
        />
        <link rel="canonical" href={`https://mycoachhub.in/${authMode === 'register' ? 'register' : 'login'}`} />
        {/* The login screen has nothing for search results; the register page does */}
        <meta name="robots" content={authMode === 'register' ? 'index, follow' : 'noindex, follow'} />
      </Helmet>
      {twoFactor && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="two-factor-title">
          <form onSubmit={handleTwoFactor} className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl dark:bg-slate-900" data-testid="two-factor-step">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h2 id="two-factor-title" className="text-lg font-bold text-slate-900 dark:text-white">Enter your sign-in code</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Open your authenticator app (Google or Microsoft Authenticator) and type the 6-digit code for CoachHub. Lost your phone? Use one of your backup codes.
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Phone me Authenticator app kholiye aur CoachHub ka 6-digit code daaliye. Phone paas nahi hai? Koi ek backup code daaliye.</p>
            <input
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.slice(0, 12))}
              autoFocus
              autoComplete="one-time-code"
              inputMode="text"
              placeholder="123456"
              aria-label="Sign-in code"
              className="mt-4 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-center font-mono text-xl tracking-[0.3em] text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              data-testid="two-factor-code"
            />
            <button
              type="submit"
              disabled={twoFactorBusy || !twoFactorCode.trim()}
              className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              data-testid="two-factor-submit"
            >
              {twoFactorBusy && <Loader2 className="h-4 w-4 animate-spin" />} Verify and sign in
            </button>
            <button
              type="button"
              onClick={() => { setTwoFactor(null); setTwoFactorCode(''); }}
              className="mt-2 h-10 w-full rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              Back
            </button>
          </form>
        </div>
      )}
      {/* ── LEFT PANEL: what CoachHub does, as a looping picture ── */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-ink via-brand-navy to-brand-navy p-10 text-white xl:p-14">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />

        <Link to="/" className="relative z-10 inline-flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/20 bg-white/10">
            <GraduationCap className="h-5 w-5 text-brand-gold" />
          </span>
          <span className="font-cabinet text-2xl font-bold tracking-tight">CoachHub</span>
        </Link>

        <div className="relative z-10 space-y-8">
          <div className="max-w-md">
            <h2 className="font-cabinet text-3xl font-extrabold leading-tight tracking-tight xl:text-4xl">
              Your whole institute, in one place
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-indigo-100/80 xl:text-base">
              Attendance, fees, exams and parent updates for schools, colleges and coaching institutes.
            </p>
          </div>
          <AuthShowcase />
        </div>

        <div className="relative z-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-indigo-100/80">
          {['Free 14-day trial', 'No card needed', 'Made in India'].map((point) => (
            <span key={point} className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" /> {point}
            </span>
          ))}
        </div>
      </div>

      {/* ── RIGHT AUTH PANEL (Form with Standard Text Boxes & Standard Dropdown) ── */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 lg:p-12 xl:p-16 bg-slate-50/70 dark:bg-slate-950 transition-colors duration-200">
        <div className="w-full max-w-md space-y-6">
          {/* Header: logo and theme, a clear title, then Login / Register */}
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-3">
              <Link to="/" className="flex min-w-0 items-center gap-2.5">
                {cachedBranding?.logo ? (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
                    <img src={cachedBranding.logo} alt="" className="max-h-full max-w-full object-contain" />
                  </span>
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-navy text-brand-gold">
                    <GraduationCap className="h-5 w-5" />
                  </span>
                )}
                <span className="truncate font-cabinet text-lg font-bold text-slate-900 dark:text-white">{cachedBranding?.name || 'CoachHub'}</span>
              </Link>
              <div className="flex h-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white px-1 shadow-xs transition-colors dark:border-slate-700/80 dark:bg-slate-900" data-testid="auth-theme-toggle">
                <ThemeToggle showDropdown={true} />
              </div>
            </div>

            <div>
              <h1 className="font-cabinet text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
                {authMode === 'login' ? 'Welcome back' : 'Create your institute workspace'}
              </h1>
              <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
                {authMode === 'login'
                  ? 'Sign in with your email, mobile number or student ID.'
                  : 'Free for 14 days. No card needed.'}
              </p>
            </div>

            <div className="grid grid-cols-2 rounded-xl border border-slate-200 bg-slate-100 p-1 transition-colors dark:border-slate-700/80 dark:bg-slate-800/90" role="tablist">
              {[['login', 'Login'], ['register', 'Register']].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={authMode === mode}
                  onClick={() => setAuthMode(mode)}
                  className={`rounded-lg py-2 text-sm font-semibold transition-all cursor-pointer ${
                    authMode === mode
                      ? 'bg-white text-slate-900 shadow-xs dark:bg-slate-900 dark:text-white'
                      : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Form Card ── */}
          <form
            onSubmit={handleSubmit}
            className="space-y-3.5 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-7 shadow-sm transition-colors"
            data-testid="auth-form"
          >
            {claimListing && (
              <div data-testid="claim-banner" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                <strong>{claimListing.name}</strong>{claimListing.city ? `, ${claimListing.city}` : ''} claim kar rahe hain.{' '}
                {authMode === 'register' ? 'Account banne ke baad hamari team listing ke phone par call karke verify karegi.' : 'Login karte hi claim review ke liye chala jaayega.'}
              </div>
            )}
            {!claimListing && authMode === 'register' && (attribution.referral_code || attribution.referred_via) && (
              <div data-testid="referral-banner" className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200">
                {attribution.referral_code ? <>Referral code <strong>{attribution.referral_code}</strong> lag gaya.</> : 'Aap ek CoachHub institute ke link se aaye hain; referral apne aap lag jaayega.'}
              </div>
            )}

            {/* If Register Mode: 3-Pillar Category Selector */}
            {authMode === 'register' && (
              <div className="space-y-3 pb-1">
                {/* Archetype Quick Buttons */}
                <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700">
                  {ARCHETYPES.map((arch) => {
                    const ArchIcon = arch.icon;
                    const isSelected = activeArchetypeId === arch.id;
                    return (
                      <button
                        key={arch.id}
                        type="button"
                        onClick={() => handleSelectArchetype(arch.id)}
                        className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-400 shadow-xs'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        <ArchIcon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{arch.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* 1. Full Name / Institute Name (No Label, Standard Text Box) */}
                <StandardInput
                  icon={Building2}
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={currentArchetype.placeholderName}
                  required
                  disabled={loading}
                  error={fieldErrors.name}
                  autoComplete="name"
                  dataTestId="register-name-input"
                  ariaLabel="Institute Name"
                />

                {/* 2. Standard Category Dropdown (StandardDropdown h-12) */}
                <StandardDropdown
                  value={form.category}
                  onChange={handleCategoryDropdownChange}
                  options={categoryDropdownOptions}
                  placeholder="Select Category (School, College, Coaching...)"
                  icon={currentArchetype.icon}
                  error={fieldErrors.category}
                  disabled={loading || categoriesLoading}
                  dataTestId="register-category-select"
                  ariaLabel="Institute Category"
                />

                {/* 3. Sub-category Standard Dropdown (if available) */}
                {availableSubcategories.length > 0 && (
                  <StandardDropdown
                    value={form.subcategory}
                    onChange={handleSubcategoryDropdownChange}
                    options={subcategoryDropdownOptions}
                    placeholder="Select Sub-category (Optional)"
                    icon={BookOpen}
                    disabled={loading}
                    dataTestId="register-subcategory-select"
                    ariaLabel="Institute Sub-category"
                  />
                )}

                {/* 4. Phone Number (No Label, Standard Text Box) */}
                <StandardInput
                  icon={Phone}
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Mobile Number (+91 98765 43210)"
                  disabled={loading}
                  error={fieldErrors.phone}
                  autoComplete="tel"
                  dataTestId="register-phone-input"
                  ariaLabel="Phone Number"
                />
              </div>
            )}

            {/* Email Address / User ID / Parent Phone (Both Login & Register) */}
            <StandardInput
              icon={Mail}
              type={authMode === 'register' ? 'email' : 'text'}
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder={
                authMode === 'login'
                  ? 'Email, mobile number or Student ID'
                  : currentArchetype.placeholderEmail
              }
              required
              disabled={loading}
              error={fieldErrors.email}
              autoComplete={authMode === 'login' ? 'username' : 'email'}
              dataTestId="email-input"
              ariaLabel="Email or User ID"
            />

            {/* Password (Both Login & Register - No Label, Standard Text Box with Eye Toggle) */}
            <StandardInput
              icon={Lock}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder={
                authMode === 'login' ? 'Enter password' : 'Set password (min 8 chars, A-Z, 0-9 & symbol)'
              }
              required
              disabled={loading}
              autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
              dataTestId="password-input"
              ariaLabel="Password"
              rightElement={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer p-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />

            {/* Cloudflare security check (only when its keys are set, and on sign-in only when asked) */}
            <Turnstile siteKey={captchaVisible ? captchaSiteKey : ''} onToken={setCaptchaToken} resetKey={captchaRound} />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (captchaVisible && !captchaToken)}
              data-testid="auth-submit-btn"
              style={{ backgroundColor: 'var(--brand-primary, #4f46e5)' }}
              className="w-full h-12 hover:opacity-95 disabled:opacity-50 text-white font-semibold rounded-xl transition-all shadow-sm text-sm flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99] mt-2"
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              <span>
                {authMode === 'login'
                  ? portalRole === 'student'
                    ? 'Sign In to Student Portal'
                    : portalRole === 'parent'
                    ? 'Sign In to Parent Portal'
                    : 'Sign in'
                  : `Register ${currentArchetype.label} Workspace`}
              </span>
            </button>

            {/* Login Auxiliary Controls */}
            {authMode === 'login' && (
              <>
                <div className="flex items-center justify-between gap-3 pt-1">
                  <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 dark:border-slate-750 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      disabled={loading}
                    />
                    Remember my login
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    data-testid="forgot-password-link"
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold cursor-pointer"
                  >
                    Forgot Password?
                  </button>
                </div>
              </>
            )}
          </form>

          {/* Modal for Forgot Password */}
          {showForgotPassword && <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />}

          {/* Bottom Switch Link */}
          <div className="text-center pt-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {authMode === 'login' ? "Don't have an institute workspace yet? " : 'Already registered? '}
              <button
                type="button"
                onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                data-testid="switch-auth-mode-btn"
                className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline cursor-pointer ml-1"
              >
                {authMode === 'login' ? 'Register Institute' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
